import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * The Losing Traffic data is fetched rather than imported. It is generated by
 * workbench/backlog/feature_watchdog/gen-fixtures.mjs into public/, where it is gitignored. It runs
 * to tens of megabytes, and in src/ that is megabytes webpack parses into an AST and Terser holds
 * again — it overran the production build's heap, and it has no business in the deployed bundle.
 *
 * A machine that has not run the generator has no file, and the pages say so rather than rendering
 * an empty table that reads as a site with no traffic.
 */
// Absolute, not PUBLIC_URL-relative: package.json sets "homepage": ".", so a relative URL would
// resolve against the route and ask for /ws/405/watchdog/.... The app's routes are absolute, so it is
// served from the root and so is public/. One file per workspace, written by the generator.
const fixturesUrlOf = (workspaceNumber) => `/watchdog/${workspaceNumber}/fixtures.json`;

const pending = new Map();

const fetchWindow = (workspaceNumber) => {
    if (!pending.has(workspaceNumber)) {
        const url = fixturesUrlOf(workspaceNumber);
        pending.set(workspaceNumber, fetch(url)
            .then((response) => {
                if (!response.ok) throw new Error(`${response.status} fetching ${url}`);
                // A build without the file answers every path with index.html and a 200, so an
                // absent fixture arrives as HTML rather than as a 404.
                const type = response.headers.get("content-type") || "";
                if (!type.includes("json")) throw new Error(`${url} is not there`);
                return response.json();
            })
            .then(({ windows }) => windows[Object.keys(windows)[0]])
            .catch((error) => { pending.delete(workspaceNumber); throw error; }));
    }
    return pending.get(workspaceNumber);
};

/** The route's workspace's window of days, or null while it is still coming, or the reason it is not coming. */
export const useFixtureWindow = () => {
    const { workspaceNumber } = useParams();
    const [state, setState] = useState({ dayWindow: null, error: null });

    useEffect(() => {
        let live = true;
        setState({ dayWindow: null, error: null });
        fetchWindow(workspaceNumber).then(
            (dayWindow) => live && setState({ dayWindow, error: null }),
            (error) => live && setState({ dayWindow: null, error: error.message }),
        );
        return () => { live = false; };
    }, [workspaceNumber]);

    return state;
};
